/**
 * EngagementWriteService.js
 * 
 * Service for writing engagement data to Firestore.
 */

// Firebase
import { collection, deleteDoc, doc, getDocs, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { generateKey } from 'utilities/Keys';
import { buildUserSummary } from 'utilities/Users';

// Constants
const ENGAGEMENTS_COLLECTION = collections.engagements;

/**
 * Creates a new engagement in Firestore.
 * 
 * @param {{ key?: string, engagement: Object }} options - Options containing an optional key and the engagement data.
 * @param {string} [options.key] - Optional key for the engagement. If not provided, a new key will be generated.
 * @param {Object} options.engagement - The engagement data to be created.
 * 
 * @returns {Promise<Object|null>} The created engagement data or null if engagement data is not provided.
 */
export async function createEngagement({ key, engagement } = {}) {

  // Validate engagement data
  if (!engagement) return null;

  // Determine engagement key
  const engagementKey = key || engagement?.key || generateKey();

  // Timestamps
  const now = Timestamp.now();

  // Prepare payload
  const payload = {
    ...engagement,
    key: engagementKey,
    dateCreated: engagement?.dateCreated || now,
    dateModified: engagement?.dateModified || now,
  };

  // Create engagement document
  await setDoc(doc(db, ENGAGEMENTS_COLLECTION, engagementKey), payload, { merge: false });

  // Return the created engagement payload
  return payload;
}

/**
 * Updates an existing engagement in Firestore.
 * 
 * @param {string} engagementKey - The key of the engagement to update.
 * @param {Object} data - The data to update the engagement with.
 * 
 * @returns {Promise<void>}
 */
export async function updateEngagement(engagementKey, data) {

  // Validate inputs
  if (!engagementKey || !data) return;

  // Prepare payload with updated dateModified
  const payload = {
    ...data,
    dateModified: data?.dateModified || Timestamp.now(),
  };

  // Update engagement document
  await updateDoc(doc(db, ENGAGEMENTS_COLLECTION, engagementKey), payload);
}

/**
 * Deletes an engagement by its key.
 * 
 * @param {string} engagementKey - The key of the engagement to delete.
 * 
 * @returns {Promise<void>} - Promise that resolves when the engagement is deleted.
 */
export async function deleteEngagementByKey(engagementKey) {

  // Validate input
  if (!engagementKey) return;

  // Delete engagement document
  await deleteDoc(doc(db, ENGAGEMENTS_COLLECTION, engagementKey));
}

/**
 * Deletes engagements based on provided parameters.
 * 
 * @param {Array<{ field: string, operator: any, value: any }>} params - Array of parameter objects to filter engagements.
 * @param {{ engagementKey?: string }} options - Options containing an optional engagementKey to filter by.
 * 
 * @returns {Promise<void>} - Promise that resolves when the engagements are deleted.
 */
export async function deleteEngagementsByParams(params = [], { engagementKey } = {}) {

  // Build query constraints
  const constraints = [];

  // Apply engagementKey filter if provided
  if (engagementKey) {
    constraints.push(where('key', '==', engagementKey));
  }

  // Apply other filters
  (params || []).forEach((param) => {
    if (param?.field && param?.operator && param?.value !== undefined) {
      constraints.push(where(param.field, param.operator, param.value));
    }
  });

  // Create query
  const q = query(collection(db, ENGAGEMENTS_COLLECTION), ...constraints);

  // Execute query
  const snap = await getDocs(q);

  // Delete matching engagements
  if (snap.empty) return;

  // Delete each document
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}

/**
 * Adds an engagement of the specified type for the current node.
 * 
 * @param {string} type - The type of engagement to add.
 * @param {Object} node - The node (item) to which the engagement relates.
 * @param {Object} user - The user performing the engagement.
 * @param {Object} [extraData={}] - Optional extra data to include in the engagement.
 * 
 * @returns {Promise<void>} - Promise that resolves when the engagement is added.
 */
export async function addEngagement(type, node, user, extraData = {}) {

  // Validate inputs
  if (!type || !node?.key || !user?.key) return;

  // Generate engagement key
  const key = generateKey();

  // Prepare engagement data
  const data = {
    key,
    type,
    itemKey: node.key,
    itemSummary: node,
    userKey: user.key,
    user: buildUserSummary(user),
    targetKey: node.userKey,
    ...extraData,
  };

  // Create engagement
  await createEngagement({ key, engagement: data });
}

/**
 * Deletes an engagement of the specified type for the current node.
 * 
 * @param {string} type - The type of engagement to delete.
 * @param {Object} node - The node (item) from which the engagement should be deleted.
 * @param {Object} user - The user whose engagement should be deleted.
 * 
 * @returns {Promise<void>} - Promise that resolves when the engagement is deleted.
 */
export async function deleteEngagement(type, node, user) {

  // Validate inputs
  if (!type || !node?.key || !user?.key) return;

  // Prepare parameters for deletion
  const params = [
    { field: 'type', operator: '==', value: type },
    { field: 'userKey', operator: '==', value: user.key },
    { field: 'itemKey', operator: '==', value: node.key },
  ];

  // Delete engagements matching the parameters
  await deleteEngagementsByParams(params);
}
